import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Input, Modal, Space, Table, Tooltip, message } from 'antd';
import axios from 'axios';
import { PRIMARY_RED } from 'constants/colors';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { setBreadcrumbItems } from "../../store/actions";
import { connect } from 'react-redux';
import moment from 'moment';

const { confirm } = Modal;

const VisicoolerList = (props) => {
  const navigate = useNavigate();
  const [visicoolers, setVisicoolers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const authUser = JSON.parse(localStorage.getItem('authUser'));
  const token = authUser?.token;

  const fetchVisicoolers = async () => {
    if (!token) {
      message.error('Authentication token not found');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/visicoolers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawData = response.data || [];
      const formatted = rawData.map((v) => ({
        ...v,
        key: v._id,
        outletName: v.outletName || 'N/A',
        ownerName: v.ownerName || 'N/A',
        state: v.state || 'N/A',
        city: v.city || 'N/A',
        visicoolerCapacity: v.visicoolerCapacity ? `${v.visicoolerCapacity} L` : 'N/A',
        visicoolerCapacity: v.visicoolerCapacity ? `${v.visicoolerCapacity} L` : 'N/A',
        visicoolerStatus: v.visicoolerStatus || v.status || 'N/A',
        date: moment(v.createdAt).format('DD MMM YYYY, hh:mm A'),
      }));

      setVisicoolers(formatted);
      setPagination((prev) => ({
        ...prev,
        total: rawData.length
      }));
    } catch (error) {
      message.error('Failed to fetch visicoolers');
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { title: "Visicoolers", link: "#" },
    { title: "Visicooler Listing", link: "#" },
  ];

  useEffect(() => {
    props.setBreadcrumbItems("Visicoolers", breadcrumbItems);
    fetchVisicoolers();
    // eslint-disable-next-line
  }, []);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDelete = async (id, outletName) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASEURL}/visicoolers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success(`Deleted visicooler: ${outletName}`);
      fetchVisicoolers();
    } catch (err) {
      message.error("Failed to delete visicooler");
    }
  };

  const showDeleteConfirm = (visicooler) => {
    confirm({
      title: `Delete visicooler for ${visicooler.outletName}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this visicooler?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(visicooler._id, visicooler.outletName);
      },
    });
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASEURL}/visicoolers/update-on-fly/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`Visicooler ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchVisicoolers();
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const showDeclineConfirm = (visicooler) => {
    confirm({
      title: `Decline visicooler for ${visicooler.outletName}?`,
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to decline this visicooler?',
      okText: 'Decline',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        changeStatus(visicooler._id, 'rejected');
      },
    });
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASEURL}/visicoolers/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'visicoolers.xlsx');
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error(error);
      message.error('Export failed');
    }
  };

  const columns = [
    {
      title: 'Outlet Name',
      dataIndex: 'outletName',
      key: 'outletName',
      render: (text) => <b>{text}</b>,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const status = text?.toLowerCase?.();
        if (status === 'approved') {
          return (
            <Space>
              <Button
                type="primary"
                size="small"
                style={{ backgroundColor: PRIMARY_RED }}
                onClick={() => changeStatus(record._id, 'approved')}
              >
                Approve
              </Button>
              <Button type="link" style={{ color: PRIMARY_RED }} onClick={() => showDeclineConfirm(record)}>
                Decline
              </Button>
            </Space>
          );
        } else {
          return (
            <span style={{ textTransform: 'capitalize' }}>
              {status}
            </span>
          );
        }
      }
    },
    {
      title: 'Requested By',
      dataIndex: 'requestedBy',
      key: 'requestedBy',
      render: (text) => text?.employeeName || 'N/A'
    },
    {
      title: 'Request To',
      dataIndex: 'requestTo',
      key: 'requestTo',
      render: (text) => text?.employeeName || 'N/A'
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Capacity',
      dataIndex: 'visicoolerCapacity',
      key: 'visicoolerCapacity',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <EditOutlined
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/edit-visicooler/${record._id}`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteOutlined
              style={{ color: PRIMARY_RED, cursor: 'pointer' }}
              onClick={() => showDeleteConfirm(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // 1. Filter before pagination
  const filteredData = visicoolers.filter((item) => {
    const search = searchText.toLowerCase();
    return (
      (item.outletName || '').toLowerCase().includes(search) ||
      (item.city || '').toLowerCase().includes(search) ||
      (item.state || '').toLowerCase().includes(search) ||
      (item.status || item.visicoolerStatus || '').toLowerCase().includes(search) ||
      (item.requestedBy?.employeeName || '').toLowerCase().includes(search) ||
      (item.requestTo?.employeeName || '').toLowerCase().includes(search)
    );
  });

  // 2. Then paginate
  const paginatedData = filteredData.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <input
          type="text"
          placeholder="Search visicooler..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 250, padding: "6px 10px" }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ backgroundColor: PRIMARY_RED }}
          onClick={() => navigate("/add-visicooler")}
        >
          Add Visicooler
        </Button>
        <Button
          type="default"
          style={{ marginLeft: 10, borderColor: PRIMARY_RED, color: PRIMARY_RED }}
          onClick={handleExport}
        >
          Export Excel
        </Button>
      </div>

      <Table
        dataSource={paginatedData}
        columns={columns}
        loading={loading}
        pagination={{
          ...pagination,
          total: filteredData.length,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) =>
            `Rows per page: ${range[0]}–${range[1]} of ${total}`,
        }}
        onChange={handleTableChange}
        bordered={false}
      />
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(VisicoolerList);
